import { useEffect, useState } from "react";
import AskAddyV2 from "../models/askAddyV2";
import { submitAnswerFeedback } from "../models/answerFeedbackModel";
import { isLegacyMessage, stripEarlierChat } from "./legacyChats";

const textOf = (message) =>
  (message?.content || [])
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("\n")
    .trim();

/**
 * The question an answer replies to, by its place in the chat (turn 0 is the first question): the
 * same while the answer streams and once the chat is reopened, unlike message ids. Classic chat
 * messages shown above a continued chat aren't counted, and their answers can't be rated.
 */
export const getAnswerTurn = (messages, messageId) => {
  const index = messages.findIndex(({ id }) => id === messageId);
  if (index === -1 || isLegacyMessage(messages[index])) {
    return null;
  }

  const questions = messages.slice(0, index).filter((message) => message.role === "user" && !isLegacyMessage(message));
  if (!questions.length) {
    return null;
  }

  return {
    turn: questions.length - 1,
    question: stripEarlierChat(textOf(questions[questions.length - 1])),
    answer: textOf(messages[index]),
  };
};

/**
 * This chat's thumbs up and down ({ [turn]: { rating, reason } }), and `rate`, which saves one (a null
 * rating takes it back). A thumbs down also goes to the team, as processing checklist feedback does.
 */
export const useAnswerFeedback = (identity, threadId, loanId) => {
  const [ratings, setRatings] = useState({});
  const { organizationId, userId } = identity;

  useEffect(() => {
    if (!organizationId || !userId || !threadId) {
      return undefined;
    }

    let cancelled = false;
    // Without them the answers just show no rating yet.
    AskAddyV2.feedback
      .list(threadId, { organizationId, userId })
      .then((saved) => !cancelled && setRatings(saved || {}))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [organizationId, userId, threadId]);

  const rate = async ({ turn, rating, reason, question, answer }) => {
    const previous = ratings[turn];
    setRatings((current) => ({ ...current, [turn]: rating ? { rating, ...(reason ? { reason } : {}) } : undefined }));
    try {
      // The team's copy first: a thumbs down that didn't reach them must not look saved.
      if (rating === "down") {
        await submitAnswerFeedback({ userId, organizationId, loanId, threadId, turn, question, answer, reason });
      }
      await AskAddyV2.feedback.rate(threadId, { turn, rating, reason, question, answer }, identity);
    } catch (error) {
      setRatings((current) => ({ ...current, [turn]: previous }));
      throw error;
    }
  };

  return { ratings, rate };
};
